import { useEffect, useState } from 'react';

import { Box } from 'components/Box/Box';
import { ButtonBar } from 'components/ButtonBar/ButtonBar';
import { EventDetailsList } from 'components/DetailsList/Event/EventDetailsList';
import { PageToolbar } from 'components/PageToolbar/PageToolbar';
import { PrintHidden } from 'components/PrintHidden/PrintHidden';
import { configValue } from 'config/appConfigUtils';
import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { useListingApiFetchRsvpData } from 'modules/listing/api/event/listingApiFetchRsvpData';
import { ApiEvent } from 'modules/listing/api/event/types/ApiEvent';
import { ListingViewActionButtons } from 'modules/listing/components/ListingView/ActionButtons/ListingViewActionButtons';
import { ListingViewAdminActionButtons } from 'modules/listing/components/ListingView/ActionButtons/ListingViewAdminActionButtons';
import { ListingViewBody } from 'modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from 'modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from 'modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from 'modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from 'modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarEventAttendees } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarEventAttendees';
import { ListingViewSidebarEventHost } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarEventHost';
import { ListingViewSidebarImage } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from 'modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from 'modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from 'modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from 'modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewCommentSection } from 'modules/listing/components/ListingView/sections/ListingViewCommentSection';
import { ListingViewLocationSection } from 'modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { RsvpAnswerContext } from 'modules/listing/hooks/useRsvpAnswer';
import { EventShowAllRsvpsModalLoadable } from 'modules/listing/pages/events/view/modals/EventShowAllRsvpsModalLoadable';
import { SearchContextHeader } from 'modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from 'modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackListing } from 'utils/analytics/trackListing';
import { isUserEventOwner } from 'utils/listings/getEventOwner';
import { isUserOrgOwner } from 'utils/listings/getOrgOwner';
import { isExternalURL } from 'utils/url/isExternalURL';
import { showModal } from 'zustand-stores/modalStore';
import { useUser } from 'zustand-stores/userEnvironmentStore';

type Props = {
  event: ApiEvent;
};

export function ViewEventPage({ event }: Props) {
  const {
    areasOfFocus,
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    id,
    startDate,
    startTime,
    directions,
    endDate,
    endTime,
    startsLocal,
    endsLocal,
    firstPublished,
    feeAmount,
    feeRequired,
    hasRsvp,
    name,
    timezone,
    welcomeGroups,
    welcomeFamilies,
    detailsWheelchairAccessible,
    type,
    image,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    isIdealistDay,
    creator,
  } = event;

  const { searchContext } = useSearchContextHeader({
    currentObjectId: id,
  });

  const org = 'org' in event ? event.org : null;

  const { user } = useUser();
  const [answer, setAnswer] = useState<string | null>(null);
  const [showRsvpsModal, setShowRsvpsModal] = useState(false);
  const isOrgOwner = isUserOrgOwner(user, org?.id);
  const isUserOwner = isUserEventOwner(user, event);
  const openwebEnabled = configValue('openwebSpotId');
  const showComments =
    openwebEnabled &&
    event.hasComments &&
    (isOrgOwner ||
      isUserOwner ||
      answer === 'GOING' ||
      answer === 'INTERESTED');

  const [rsvpDataResult, { reload, loading: rsvpDataLoading }] =
    useListingApiFetchRsvpData(event.id);

  useSetDocumentTitle(name);

  useEffect(() => {
    // Refresh RSVP data when current user's own RSVP answer changes
    if (answer) reload();
  }, [answer, reload]);

  useEffect(() => {
    trackListing({
      hasAts: false,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [id, name, org?.id, org?.name, org?.orgType, type]);

  return (
    <RsvpAnswerContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        answer,
        setAnswer,
      }}
    >
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={getText('Your organization posted this event')}
          tools={
            <ListingViewAdminActionButtons
              listingId={id}
              listingType="EVENT"
              orgId={org.id}
              canPost={Boolean(
                user?.orgs.find((o) => o.id === org.id)?.canPostEvents,
              )}
              hasAts={false}
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            isPostedAnonymously={false}
            orgLogo={org?.logo}
            orgName={org?.name}
            orgURLForCurrentLocale={org?.url[CURRENT_LOCALE]}
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="EVENT"
            orgName={org?.name}
            orgUrl={org?.url}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            listingId={id}
            listingType={type}
            hasAts={false}
          />

          {hasRsvp && (
            <ListingViewSidebarEventAttendees
              loading={rsvpDataLoading}
              rsvps={rsvpDataResult?.rsvps}
              count={rsvpDataResult?.count}
              onClick={() => setShowRsvpsModal(true)}
            />
          )}

          {hasRsvp && creator && (
            <ListingViewSidebarEventHost creator={creator} />
          )}

          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={org?.url}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            hasAts={false}
            hasRsvp={hasRsvp}
            isPostedAnonymously={false}
            listingId={id}
            listingName={name}
            listingType={type}
            listingUrl={url}
            listingFirstPublished={firstPublished}
            orgId={org?.id}
            orgName={org?.name}
            orgType={org?.orgType}
            orgUrl={org?.url}
            remoteOptions={remoteOptions}
            isIdealistDay={isIdealistDay}
          />

          <ListingViewSection title={getText('Details')} noBorderTop>
            <EventDetailsList
              areasOfFocus={areasOfFocus}
              startDate={startDate}
              startTime={startTime}
              endDate={endDate}
              endTime={endTime}
              startsLocal={startsLocal}
              endsLocal={endsLocal}
              feeAmount={feeAmount}
              feeRequired={feeRequired}
              timezone={timezone}
              welcomeGroups={welcomeGroups}
              welcomeFamilies={welcomeFamilies}
              detailsWheelchairAccessible={detailsWheelchairAccessible}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={image}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: [],
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'EVENT',
                });
              }
            }}
            showMoreLinkTitle={getText('Read more about this event')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: event.type,
                locale: event.locale,
              });
            }}
          />

          <ListingViewLocationSection
            listingType={type}
            address={address}
            directions={directions}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            applyEmail={applyEmail}
            applyText={applyText}
            applyTextTruncated={applyTextTruncated}
            applyUrl={applyUrl}
            functions={[]}
            hasAts={false}
            listingName={name}
            listingType="EVENT"
            listingId={id}
            orgType={org?.orgType}
            orgId={org?.id}
            requestedAttachments={[]}
            requestedAttachmentsHash=""
            synonym={null}
          />

          {showComments && <ListingViewCommentSection listing={event} />}

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta
              listingUrl={url[CURRENT_LOCALE]}
              type={type}
            />
          </ListingViewFooter>
        </PrintHidden>
      )}

      <ButtonBar>
        <ListingViewActionButtons
          hasAts={false}
          hasRsvp={hasRsvp}
          listingId={id}
          listingName={name}
          listingType={type}
          listingUrl={url}
          orgId={org?.id}
          orgName={org?.name}
          orgType={org?.orgType}
          mobile
        />
      </ButtonBar>

      {showRsvpsModal && rsvpDataResult && (
        <EventShowAllRsvpsModalLoadable
          eventId={id}
          count={rsvpDataResult.count}
          initialRsvps={rsvpDataResult.rsvps}
          onClose={() => setShowRsvpsModal(false)}
        />
      )}
    </RsvpAnswerContext.Provider>
  );
}
